import React from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  Select,
  Autocomplete,
  Typography,
  alpha,
  Divider,
} from "@mui/material";
import axios from "axios";
import { showToast, WEBSERVICE } from "../../CoreDataService";
import { countries } from "../constants";
import {
  allowOnlyAlphabetsCityState,
  OnlyAllowedInput,
  OnlyAllowedInputHouse,
  OnlyAllowedInputStreet,
  OnlyAllowedZipCode,
  PreventPasteStreet,
  PreventPasteHouse,
  PreventPasteCityState,
  sanitizeBlockedUnicode,
  handleBlockedUnicodePaste,
} from "./index";
import { useSelector } from "react-redux";
import { ContainedButton } from "../../Common/Buttons";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import MuiCountryStateCityFields from "./CountryStateCityFields";

const IdentityVerificationAddress = ({
  handleSteps,
  handleBack,
  userDetails,
  setIsloading,
}) => {
  const { btncolor } = useSelector((state) => state);
  const uuid = localStorage.getItem("uuid");
  const token = localStorage.getItem("access_token");

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    control,
    formState: { errors, isValid },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      houseNo: userDetails?.address?.split(",")[0]?.trim(),
      streetArea: userDetails?.address?.split(",").slice(1).join(",").trim(),
      country: userDetails?.country || "",
      state: userDetails?.state || "",
      city: userDetails?.city || "",
      zipCode: userDetails?.zip,
    },
  });

  const onSubmit = async (data) => {
    const payload = {
      ...userDetails,
      address: `${sanitizeBlockedUnicode(data?.houseNo)},${sanitizeBlockedUnicode(data?.streetArea)}`,
      city: sanitizeBlockedUnicode(data?.city),
      state: sanitizeBlockedUnicode(data?.state),
      country: sanitizeBlockedUnicode(data?.country),
      zip: sanitizeBlockedUnicode(data?.zipCode),
      uuid: uuid,
    };
    setIsloading(true);
    try {
      const response = await axios.post(
        `${WEBSERVICE}user/saveUserKycData`,
        payload,
        { headers: { Authorization: `BEARER ${token}` } }
      );
      if (response?.data?.error?.error_data === 0) {
        setIsloading(false);
        showToast("success", response?.data?.error?.error_msg);
        handleSteps();
      } else {
        setIsloading(false);
        showToast("error", response?.data?.error?.error_msg);
      }
    } catch (error) {
      setIsloading(false);
      showToast("error", error?.response?.data?.error?.error_msg);
    }
  };

  return (
    <Box sx={card}>
      {/* Step Header */}
      <Box sx={stepHeader}>
        <Box sx={{ ...stepBadge, backgroundColor: alpha(btncolor || "#1976d2", 0.12), color: btncolor }}>
          <LocationOnOutlinedIcon sx={{ fontSize: 18 }} />
        </Box>
        <Box>
          <Typography sx={stepTitle}>Address Details</Typography>
          <Typography sx={stepCaption}>
            This address should match your address proof document
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ borderColor: "rgba(125,125,125,0.13)", mb: 3 }} />

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Grid container spacing={2.5}>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Typography sx={fieldLabel}>House No. / Apt No. *</Typography>
            <TextField
              fullWidth
              autoComplete="off"
              variant="outlined"
              placeholder="Enter house / apt / street no."
              error={!!errors.houseNo}
              helperText={errors.houseNo?.message}
              {...register("houseNo", { required: "This field is required" })}
              onKeyDown={OnlyAllowedInputHouse}
              onPaste={PreventPasteHouse}
              sx={modernField}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Typography sx={fieldLabel}>Street / Area *</Typography>
            <TextField
              fullWidth
              autoComplete="off"
              variant="outlined"
              placeholder="Street / Area"
              error={!!errors.streetArea}
              helperText={errors.streetArea?.message}
              {...register("streetArea", { required: "This field is required" })}
              onKeyDown={OnlyAllowedInputStreet}
              onPaste={PreventPasteStreet}
              sx={modernField}
            />
          </Grid>

          <MuiCountryStateCityFields
            control={control}
            setValue={setValue}
            watch={watch}
            errors={errors}
            countryKey="country"
            stateKey="state"
            cityKey="city"
            labelType="typography"
            labelSx={fieldLabel}
            fieldStyle={modernField}
            gridSize={{ xs: 12, sm: 6, md: 4 }}
          />

          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Typography sx={fieldLabel}>Zip Code / Pin Code *</Typography>
            <TextField
              fullWidth
              autoComplete="off"
              variant="outlined"
              placeholder="Enter zip / pin code"
              error={!!errors.zipCode}
              helperText={errors.zipCode?.message}
              inputProps={{ maxLength: 15 }}
              {...register("zipCode", { required: "Zip Code is required" })}
              onKeyDown={OnlyAllowedZipCode}
              onPaste={handleBlockedUnicodePaste}
              sx={modernField}
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 3.5, pt: 2.5, borderTop: "1px solid rgba(125,125,125,0.12)", display: "flex", gap: 1.5 }}>
          <ContainedButton
            type="submit"
            variant="contained"
            disabled={!isValid}
            sx={submitBtn}
          >
            Save and Next
          </ContainedButton>
        </Box>
      </form>
    </Box>
  );
};

// ── Styles ────────────────────────────────────────────────────────────────────

const card = {
  backgroundColor: "var(--body-color-less)",
  borderRadius: "16px",
  border: "1px solid rgba(125,125,125,0.15)",
  boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 10px 25px rgba(0,0,0,0.06)",
  p: { xs: 2.5, md: 3 },
};

const stepHeader = {
  display: "flex",
  alignItems: "center",
  gap: 1.5,
  mb: 2.5,
  flexWrap: "wrap",
};

const stepBadge = {
  width: 44,
  height: 44,
  borderRadius: "12px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

const stepTitle = {
  fontSize: "1rem",
  fontWeight: 700,
  color: "var(--text-color)",
  lineHeight: 1.2,
};

const stepCaption = {
  fontSize: "0.78rem",
  color: "var(--text-color)",
  opacity: 0.55,
  mt: 0.2,
};

const stepNum = {
  ml: "auto",
  fontSize: "0.7rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  px: 1.5,
  py: 0.6,
  borderRadius: "20px",
};

const fieldLabel = {
  fontSize: "0.72rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  color: "var(--text-color)",
  opacity: 0.55,
  mb: 0.75,
};

const modernField = {
  "& .MuiInputBase-root": {
    height: "48px",
    borderRadius: "10px",
    backgroundColor: "var(--body-color-less2)",
    color: "var(--text-color)",
    fontSize: "0.9rem",
  },
  "& .MuiInputBase-input": { color: "var(--text-color)" },
  "& .MuiSelect-select": { color: "var(--text-color)" },
  "& .MuiSvgIcon-root": { color: "var(--text-color)", opacity: 0.6 },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(125,125,125,0.25)" },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(125,125,125,0.45)" },
  "& .MuiInputLabel-root": { display: "none" },
  "& legend": { display: "none" },
  "& fieldset": { top: 0 },
  "& .MuiFormHelperText-root": { color: "#ef4444" },
};

const selectInner = {
  height: "48px",
  borderRadius: "10px",
  backgroundColor: "var(--body-color-less2)",
  color: "var(--text-color)",
  "& .MuiSvgIcon-root": { color: "var(--text-color)", opacity: 0.6 },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(125,125,125,0.25)" },
  "& fieldset": { top: 0 },
  "& legend": { display: "none" },
};

const submitBtn = {
  height: 48,
  px: 4,
  borderRadius: "10px",
  fontWeight: 700,
  fontSize: "0.88rem",
  textTransform: "none",
};

export default IdentityVerificationAddress;
